import { EditOutlined, KeyOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Form, Input, List, Modal, Select, Switch, Tag, Typography } from 'antd';
import axios from 'axios';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../../api/client';
import { storesApi, usersApi } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { CreateUserBody, ManagedUser, Role, UpdateUserBody } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ModalMultiSelect } from '../../components/ModalMultiSelect';
import { ModalSelect } from '../../components/ModalSelect';
import { ErrorBlock, LoadingBlock } from '../../components/PageStates';

interface UserFormValues {
  login: string;
  name: string;
  password?: string;
  role: Role;
  isActive?: boolean;
  storeId?: string;
  storeIds?: string[];
}

/** User management (superadmin only). Mirrors the DirectoryTab list+modal
 * pattern. Backend enforces last-superadmin / self-lockout rules — the UI just
 * surfaces the resulting error messages. */
export function UsersTab(): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { user: me } = useAuth();
  const [editing, setEditing] = useState<ManagedUser | null>(null);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<ManagedUser | null>(null);
  const [form] = Form.useForm<UserFormValues>();
  const [resetForm] = Form.useForm<{ newPassword: string }>();

  const query = useQuery({ queryKey: queryKeys.users, queryFn: usersApi.list });
  const storesQuery = useQuery({
    queryKey: queryKeys.stores(false),
    queryFn: () => storesApi.list(false),
  });
  const storeOptions = (storesQuery.data ?? []).map((s) => ({ value: s.id, label: s.name }));
  const storeName = (id: string | null): string | null =>
    id ? (storesQuery.data?.find((s) => s.id === id)?.name ?? id) : null;

  // The role currently chosen in the form — a SELLER needs a store binding.
  const watchedRole = Form.useWatch('role', form);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.users });
  };

  const errorText = (error: unknown): string => {
    const code = axios.isAxiosError(error)
      ? (error.response?.data as { message?: string } | undefined)?.message
      : undefined;
    const map: Record<string, string> = {
      LOGIN_TAKEN: t('settings.users.loginTaken'),
      CANNOT_DEACTIVATE_SELF: t('settings.users.cannotDeactivateSelf'),
      CANNOT_DEMOTE_SELF: t('settings.users.cannotDemoteSelf'),
      LAST_SUPERADMIN: t('settings.users.lastSuperadmin'),
      SELLER_STORE_REQUIRED: t('settings.users.sellerStoreRequired'),
      STORE_NOT_FOUND: t('settings.users.storeNotFound'),
      STORE_ARCHIVED: t('settings.users.storeArchived'),
    };
    return (
      (code && map[code]) ?? apiErrorMessage(error) ?? t('common.requestError')
    );
  };

  const saveMutation = useMutation<ManagedUser, Error, UserFormValues>({
    mutationFn: (values) => {
      const storeId = values.role === 'SELLER' ? values.storeId : undefined;
      // Always sent for USER so an emptied list CLEARS the restriction on edit.
      const storeIds = values.role === 'USER' ? { storeIds: values.storeIds ?? [] } : {};
      if (editing) {
        const body: UpdateUserBody = {
          name: values.name.trim(),
          role: values.role,
          isActive: values.isActive ?? true,
          ...(storeId ? { storeId } : {}),
          ...storeIds,
        };
        return usersApi.update(editing.id, body);
      }
      const body: CreateUserBody = {
        login: values.login.trim(),
        name: values.name.trim(),
        password: values.password ?? '',
        role: values.role,
        ...(storeId ? { storeId } : {}),
        ...storeIds,
      };
      return usersApi.create(body);
    },
    onSuccess: () => {
      message.success(editing ? t('common.saved') : t('settings.users.created'));
      invalidate();
      setEditing(null);
      setCreating(false);
    },
    onError: (error) => message.error(errorText(error)),
  });

  const resetMutation = useMutation<{ ok: true }, Error, { newPassword: string }>({
    mutationFn: (values) => usersApi.resetPassword(resetting!.id, values.newPassword),
    onSuccess: () => {
      message.success(t('settings.users.passwordReset'));
      setResetting(null);
    },
    onError: (error) => message.error(errorText(error)),
  });

  const openModal = (item: ManagedUser | null): void => {
    setEditing(item);
    setCreating(item === null);
    form.setFieldsValue({
      login: item?.login ?? '',
      name: item?.name ?? '',
      password: '',
      role: item?.role ?? 'USER',
      isActive: item?.isActive ?? true,
      storeId: item?.storeId ?? undefined,
      storeIds: item?.storeIds ?? [],
    });
  };

  const openReset = (item: ManagedUser): void => {
    setResetting(item);
    resetForm.setFieldsValue({ newPassword: '' });
  };

  const roleOptions = [
    { value: 'USER', label: t('settings.users.roleUser') },
    { value: 'SUPERADMIN', label: t('settings.users.roleSuperadmin') },
    { value: 'SELLER', label: t('settings.users.roleSeller') },
  ];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => openModal(null)}>
        {t('settings.users.add')}
      </Button>

      {query.isLoading ? (
        <LoadingBlock />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => void query.refetch()} />
      ) : (
        <Card size="small" styles={{ body: { paddingTop: 0, paddingBottom: 0 } }}>
          <List
            dataSource={query.data}
            renderItem={(item) => (
              <List.Item
                style={{ paddingInline: 4 }}
                extra={
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      icon={<KeyOutlined />}
                      onClick={() => openReset(item)}
                      aria-label={t('settings.users.resetPassword')}
                    />
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => openModal(item)}
                      aria-label={t('common.edit')}
                    />
                  </div>
                }
              >
                <List.Item.Meta
                  title={
                    <>
                      {item.name}
                      {item.id === me?.id ? (
                        <Tag color="blue" style={{ marginInlineStart: 8 }}>
                          {t('settings.users.youTag')}
                        </Tag>
                      ) : null}
                      {!item.isActive ? (
                        <Tag style={{ marginInlineStart: 8 }}>{t('settings.users.inactiveTag')}</Tag>
                      ) : null}
                    </>
                  }
                  description={
                    <Typography.Text type="secondary">
                      {item.login} ·{' '}
                      {item.role === 'SUPERADMIN'
                        ? t('settings.users.roleSuperadmin')
                        : item.role === 'SELLER'
                          ? t('settings.users.roleSeller')
                          : t('settings.users.roleUser')}
                      {item.role === 'SELLER' && storeName(item.storeId)
                        ? ` · ${storeName(item.storeId)}`
                        : ''}
                      {item.role === 'USER' && item.storeIds.length > 0
                        ? ` · ${item.storeIds.map((id) => storeName(id)).join(', ')}`
                        : ''}
                    </Typography.Text>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      <Modal
        open={creating || editing !== null}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        title={editing?.name ?? t('settings.users.add')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={saveMutation.isPending}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form<UserFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => saveMutation.mutate(values)}
        >
          {!editing ? (
            <Form.Item
              name="login"
              label={t('settings.users.login')}
              rules={[{ required: true, whitespace: true, message: t('settings.users.loginMin') }]}
            >
              <Input size="large" maxLength={64} autoComplete="off" />
            </Form.Item>
          ) : null}
          <Form.Item
            name="name"
            label={t('settings.users.displayName')}
            rules={[{ required: true, whitespace: true, message: t('common.nameRequired') }]}
          >
            <Input size="large" maxLength={200} />
          </Form.Item>
          {!editing ? (
            <Form.Item
              name="password"
              label={t('settings.users.password')}
              rules={[
                { required: true, message: t('settings.passwordRequired') },
                { min: 8, message: t('settings.passwordMin') },
              ]}
            >
              <Input.Password size="large" autoComplete="new-password" />
            </Form.Item>
          ) : null}
          <Form.Item name="role" label={t('settings.users.role')}>
            <Select size="large" options={roleOptions} />
          </Form.Item>
          {watchedRole === 'SELLER' ? (
            <Form.Item
              name="storeId"
              label={t('settings.users.store')}
              rules={[{ required: true, message: t('settings.users.sellerStoreRequired') }]}
            >
              <ModalSelect
                size="large"
                placeholder={t('settings.users.storePlaceholder')}
                title={t('settings.users.store')}
                options={storeOptions}
                loading={storesQuery.isLoading}
              />
            </Form.Item>
          ) : null}
          {watchedRole === 'USER' ? (
            <Form.Item
              name="storeIds"
              label={t('settings.users.allowedStores')}
              extra={t('settings.users.allowedStoresHint')}
            >
              <ModalMultiSelect
                size="large"
                placeholder={t('settings.users.allowedStoresPlaceholder')}
                title={t('settings.users.allowedStores')}
                options={storeOptions}
                loading={storesQuery.isLoading}
              />
            </Form.Item>
          ) : null}
          {editing ? (
            <Form.Item name="isActive" label={t('settings.users.active')} valuePropName="checked">
              <Switch />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>

      <Modal
        open={resetting !== null}
        onCancel={() => setResetting(null)}
        title={t('settings.users.resetPasswordFor', { name: resetting?.name ?? '' })}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={resetMutation.isPending}
        onOk={() => resetForm.submit()}
        destroyOnClose
      >
        <Form<{ newPassword: string }>
          form={resetForm}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => resetMutation.mutate(values)}
        >
          <Form.Item
            name="newPassword"
            label={t('settings.users.newPassword')}
            rules={[
              { required: true, message: t('settings.passwordRequired') },
              { min: 8, message: t('settings.passwordMin') },
            ]}
          >
            <Input.Password size="large" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
